import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Bell, ChevronRight, KeyRound, Landmark, LogOut, Pencil, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth, useMembers } from "@/context/AuthContext";
import { Avatar, Field, HsId, PageHeader, Section, StatusBadge } from "@/components/kit";

const BROKERS = ["Zerodha", "HDFC Securities", "XS Direct", "Angel One", "Other"];

function FormDialog({ open, onClose, title, children }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[440px] rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">{children}</div>
      </DialogContent>
    </Dialog>
  );
}

function useSave(fn, done) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await fn(); toast.success("Saved"); done(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return [busy, run];
}

function CoparcenerDialog({ edit, onClose, onSaved }) {
  const [f, setF] = useState({ full_name: edit?.full_name || "", relation: edit?.relation || "", mobile: edit?.mobile || "", email: edit?.email || "", status: edit?.status || "active" });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, save] = useSave(() => (edit?.id ? api.put(`/coparceners/${edit.id}`, f) : api.post("/coparceners", f)), () => { onSaved(); onClose(); });
  return (
    <FormDialog open onClose={onClose} title={edit?.id ? `Edit ${edit.full_name}` : "Add Coparcener"}>
      {edit?.hs_id && <div className="text-xs text-[#718096]">HisseDari ID <HsId value={edit.hs_id} /> — permanent, never changes</div>}
      <Field label="Full name"><input data-testid="coparcener-name-input" className="hd-input" value={f.full_name} onChange={up("full_name")} /></Field>
      <Field label="Relation"><input data-testid="coparcener-relation-input" className="hd-input" value={f.relation} onChange={up("relation")} placeholder="Wife, Mother, Son…" /></Field>
      <Field label="Mobile (optional)"><input data-testid="coparcener-mobile-input" className="hd-input" value={f.mobile} onChange={up("mobile")} /></Field>
      <Field label="Email (optional)"><input data-testid="coparcener-email-input" className="hd-input" value={f.email} onChange={up("email")} /></Field>
      {edit?.id && (
        <Field label="Status">
          <select data-testid="coparcener-status-select" className="hd-input" value={f.status} onChange={up("status")}><option value="active">Active</option><option value="inactive">Inactive (deactivated)</option></select>
        </Field>
      )}
      <button className="hd-btn-primary w-full" disabled={busy || !f.full_name.trim()} onClick={save} data-testid="coparcener-save-button">{edit?.id ? "Save" : "Add Coparcener"}</button>
    </FormDialog>
  );
}

function DematDialog({ edit, holders, gid, onClose, onSaved }) {
  const [f, setF] = useState({ account_name: edit?.account_name || "", holder_person_id: edit?.holder_person_id || holders[0]?.person_id || "", broker: edit?.broker || "Zerodha", account_ref: edit?.account_ref || "", status: edit?.status || "active" });
  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, save] = useSave(() => (edit?.id ? api.put(`/demat-accounts/${edit.id}`, f) : api.post(`/groups/${gid}/demat-accounts`, f)), () => { onSaved(); onClose(); });
  return (
    <FormDialog open onClose={onClose} title={edit?.id ? "Edit Demat account" : "Add Demat account"}>
      <Field label="Account name"><input data-testid="demat-name-input" className="hd-input" value={f.account_name} onChange={up("account_name")} placeholder="Vishal Zerodha" /></Field>
      <Field label="Account holder"><select data-testid="demat-holder-select" className="hd-input" value={f.holder_person_id} onChange={up("holder_person_id")}>{holders.map((h) => <option key={h.person_id} value={h.person_id}>{h.full_name} ({h.hs_id})</option>)}</select></Field>
      <Field label="Broker"><select data-testid="demat-broker-select" className="hd-input" value={f.broker} onChange={up("broker")}>{BROKERS.map((b) => <option key={b}>{b}</option>)}</select></Field>
      <Field label="Account reference (optional)"><input data-testid="demat-ref-input" className="hd-input" value={f.account_ref} onChange={up("account_ref")} placeholder="Client ID / last 4 digits" /></Field>
      <Field label="Status"><select data-testid="demat-status-select" className="hd-input" value={f.status} onChange={up("status")}><option value="active">Active</option><option value="inactive">Inactive</option></select></Field>
      <button className="hd-btn-primary w-full" disabled={busy || !f.account_name.trim()} onClick={save} data-testid="demat-save-button">Save</button>
    </FormDialog>
  );
}

function ProfileDialog({ person, onClose, onSaved }) {
  const [f, setF] = useState({ full_name: person.full_name, mobile: person.mobile || "" });
  const [busy, save] = useSave(() => api.put("/persons/me", f), () => { onSaved(); onClose(); });
  return (
    <FormDialog open onClose={onClose} title="Edit profile">
      <Field label="Full name"><input data-testid="profile-name-input" className="hd-input" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} /></Field>
      <Field label="Mobile"><input data-testid="profile-mobile-input" className="hd-input" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} /></Field>
      <button className="hd-btn-primary w-full" disabled={busy} onClick={save} data-testid="profile-save-button">Save</button>
    </FormDialog>
  );
}

function PasswordDialog({ onClose }) {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, save] = useSave(() => api.post("/auth/change-password", f), onClose);
  return (
    <FormDialog open onClose={onClose} title="Change password">
      <Field label="Current password"><input data-testid="current-password-input" type="password" className="hd-input" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></Field>
      <Field label="New password" hint="At least 8 characters, including a number"><input data-testid="new-password-input" type="password" className="hd-input" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} /></Field>
      <button className="hd-btn-primary w-full" disabled={busy} onClick={save} data-testid="password-save-button">Update password</button>
    </FormDialog>
  );
}

export default function Profile() {
  const { person, auth, group, gid, logout, reload } = useAuth();
  const { members, reload: reloadMembers } = useMembers();
  const [cops, setCops] = useState([]);
  const [demats, setDemats] = useState([]);
  const [dlg, setDlg] = useState(null);
  const loadCops = () => api.get("/coparceners").then((r) => setCops(r.data));
  const loadDemats = () => api.get(`/groups/${gid}/demat-accounts`).then((r) => setDemats(r.data));
  useEffect(() => {
    loadCops();
    loadDemats();
  }, [gid]); // eslint-disable-line react-hooks/exhaustive-deps
  const holders = members.filter((m) => m.side_karta_id === person.id && m.status === "active");
  const myDemats = demats.filter((d) => d.karta_person_id === person.id);
  const otherDemats = demats.filter((d) => d.karta_person_id !== person.id);
  return (
    <div className="pb-6">
      <PageHeader title="Profile" back={false} />
      <div className="hd-card text-center py-6" data-testid="my-profile-card">
        <div className="flex justify-center"><Avatar name={person.full_name} id={person.id} size={64} /></div>
        <div className="text-xl font-bold mt-3" data-testid="my-profile-name">{person.full_name}</div>
        <div className="mt-2 flex justify-center"><HsId value={person.hs_id} big testid="my-hs-id" /></div>
        <div className="text-sm text-[#718096] mt-2">Karta · {group?.name}</div>
        <div className="text-sm text-[#718096]">{auth.email}{person.mobile ? ` · ${person.mobile}` : ""}</div>
        <div className="flex gap-2 justify-center mt-4">
          <button className="hd-btn-outline h-10 text-sm" onClick={() => setDlg({ k: "profile" })} data-testid="edit-profile-button"><Pencil size={15} /> Edit</button>
          <button className="hd-btn-outline h-10 text-sm" onClick={() => setDlg({ k: "password" })} data-testid="change-password-button"><KeyRound size={15} /> Password</button>
        </div>
      </div>

      <Section title={`My Coparceners (${cops.length})`} action={<button className="text-sm font-semibold text-[#C8553D] flex items-center gap-1" onClick={() => setDlg({ k: "cop" })} data-testid="add-coparcener-button"><UserPlus size={16} /> Add</button>}>
        <div className="hd-card py-1" data-testid="coparceners-list">
          {cops.length === 0 && <div className="text-sm text-[#718096] py-3">No Coparceners yet. Add family members whose investments you manage.</div>}
          {cops.map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-3 border-b border-[#E2D9CF]/50 last:border-0" data-testid={`coparcener-${c.hs_id}`}>
              <Avatar name={c.full_name} id={c.id} size={38} />
              <Link to={`/person/${c.id}`} className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{c.full_name}</div>
                <div className="flex items-center gap-1.5 mt-0.5"><HsId value={c.hs_id} /><span className="text-[11px] text-[#718096]">{c.relation || "Coparcener"}</span></div>
              </Link>
              {c.status !== "active" && <StatusBadge status={c.status} />}
              <button className="w-9 h-9 rounded-full hover:bg-[#F5F0E9] flex items-center justify-center" onClick={() => setDlg({ k: "cop", edit: c })} data-testid={`edit-coparcener-${c.hs_id}`}><Pencil size={15} /></button>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Demat accounts" action={<button className="text-sm font-semibold text-[#C8553D] flex items-center gap-1" onClick={() => setDlg({ k: "demat" })} data-testid="add-demat-button"><Plus size={16} /> Add</button>}>
        <div className="hd-card py-1" data-testid="demat-list">
          {myDemats.length === 0 && <div className="text-sm text-[#718096] py-3">No Demat accounts yet.</div>}
          {myDemats.map((d) => (
            <div key={d.id} className="flex items-center gap-3 py-3 border-b border-[#E2D9CF]/50 last:border-0" data-testid={`demat-${d.id}`}>
              <div className="w-10 h-10 rounded-xl bg-[#F5F0E9] flex items-center justify-center"><Landmark size={18} /></div>
              <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{d.account_name}</div><div className="text-xs text-[#718096]">{d.broker} · {d.holder_name} <span className="hs-id">{d.holder_hs_id}</span>{d.account_ref ? ` · ${d.account_ref}` : ""}</div></div>
              {d.status !== "active" && <StatusBadge status={d.status} />}
              <button className="w-9 h-9 rounded-full hover:bg-[#F5F0E9] flex items-center justify-center" onClick={() => setDlg({ k: "demat", edit: d })} data-testid={`edit-demat-${d.id}`}><Pencil size={15} /></button>
            </div>
          ))}
        </div>
        {otherDemats.length > 0 && <div className="text-xs text-[#718096] mt-2">{otherDemats.length} Demat account(s) managed by the other Karta are available for custody selection.</div>}
      </Section>

      <Section title="More">
        <div className="hd-card py-1">
          {[["/pending", "Pending confirmations", Bell], ["/activity", "Activity log", Activity], [`/person/${person.id}`, "My public profile", ChevronRight]].map(([to, l, I]) => (
            <Link key={to} to={to} className="flex items-center gap-3 py-3.5 border-b border-[#E2D9CF]/50 last:border-0 text-sm font-semibold" data-testid={`profile-link-${l.split(" ")[0].toLowerCase()}`}><I size={17} className="text-[#C8553D]" /> {l}<ChevronRight size={16} className="ml-auto text-[#A0AEC0]" /></Link>
          ))}
          <button onClick={logout} className="flex items-center gap-3 py-3.5 text-sm font-semibold text-red-700 w-full" data-testid="logout-button"><LogOut size={17} /> Log out</button>
        </div>
      </Section>

      {dlg?.k === "cop" && <CoparcenerDialog edit={dlg.edit} onClose={() => setDlg(null)} onSaved={() => { loadCops(); reloadMembers(); }} />}
      {dlg?.k === "demat" && <DematDialog edit={dlg.edit} holders={holders} gid={gid} onClose={() => setDlg(null)} onSaved={loadDemats} />}
      {dlg?.k === "profile" && <ProfileDialog person={person} onClose={() => setDlg(null)} onSaved={reload} />}
      {dlg?.k === "password" && <PasswordDialog onClose={() => setDlg(null)} />}
    </div>
  );
}
